import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNGConfig } from 'primeng/api';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AlertService, AuthenticationService } from '../_services';
import { Router, ActivatedRoute } from '@angular/router';
import { first } from 'rxjs/operators';
import * as CryptoJS from "crypto-js";
import { Usuario } from '../shared/models/Usuario';
import { ApiService } from '../api.service';
import { TovpilLangService } from '../utils/tovpil-lang.service';
import { AngularFireAnalytics } from '@angular/fire/analytics';


@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {

  /* These are properties of the LoginComponent class. */
  loginForm: FormGroup;
  loading = false;
  submitted = false;
  returnUrl: string;
  msj: string;
  user: Usuario;
  images: string[];
  responsiveOptions;
  responseCode: string = "";

  selectedLang: { name: "English", value: "en" };
  listLang: { name: string; value: string; }[];


  /**
   * The function returns the controls of the loginForm.
   * @returns The `f()` function is returning the `controls` property of the `loginForm` object.
   */
  get f() { return this.loginForm.controls; }

  /**
   * The constructor function initializes various dependencies and sets up responsive options and
   * language settings.
   * @param {FormBuilder} formBuilder - An instance of the FormBuilder class, which is used to create and
   * manage form controls in Angular.
   * @param {TranslateService} translate - The `translate` parameter is an instance of the
   * `TranslateService` class, which is used for internationalization and localization in the
   * application. It provides methods for translating text and managing language settings.
   * @param {AlertService} alertService - An instance of the AlertService class, which is used to display
   * alert messages to the user.
   * @param {PrimeNGConfig} primeNGConfig - The `primeNGConfig` parameter is an instance of the
   * `PrimeNGConfig` class. It is used to configure and customize the PrimeNG library components in your
   * application.
   * @param {ActivatedRoute} route - The `route` parameter is an instance of the `ActivatedRoute` class,
   * which is used to access the current route information. It provides access to the route parameters,
   * query parameters, and other route-related data.
   * @param {Router} router - The `router` parameter is an instance of the `Router` class from the
   * Angular Router module. It is used for navigating between different routes in your application.
   * @param {ApiService} apiService - An instance of the ApiService class, which is responsible for
   * making HTTP requests to the API.
   * @param {ProductService} productService - The `productService` parameter is an instance of a service
   * that is responsible for managing product-related operations, such as fetching product data from an
   * API, adding new products, updating existing products, and deleting products. It is injected into the
   * constructor using dependency injection.
   * @param {AuthenticationService} authenticationService - An instance of the AuthenticationService
   * class, which is responsible for handling user authentication and authorization.
   */
  constructor(
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    public primeNGConfig: PrimeNGConfig,
    private route: ActivatedRoute,
    private router: Router,
    public translate: TranslateService,
    private tovpilLang: TovpilLangService,
    private apiService: ApiService,
    private authenticationService: AuthenticationService,
    private analytics: AngularFireAnalytics
  ) {
    this.responsiveOptions = [
      {
        breakpoint: '1024px',
        numVisible: 1,
        numScroll: 1
      },
      {
        breakpoint: '768px',
        numVisible: 1,
        numScroll: 1
      },
      {
        breakpoint: '560px',
        numVisible: 1,
        numScroll: 1
      }
    ];

    this.listLang = [
      { name: "English", value: "en" },
      { name: "Español", value: "es" },
    ]

    const browserLang = translate.getBrowserLang();
    let lang = browserLang.match(/en|es|fr/) ? browserLang : 'es';
    this.tovpilLang.translate(lang);
  }

  /**
   * The ngOnInit function initializes the login form, sets the returnUrl, and retrieves a list of
   * products.
   */
  ngOnInit() {
    this.analytics.logEvent('login_event_responsico', {"test":"soporteti"});
    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required]
    });

    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';

    this.images = [
      "tovpil_image1.jpg",
      "tovpil_image2.jpg",
      "tovpil_image3.jpg"
    ]
  }

  /**
   * The onSubmit function is used to handle form submission, validate the form inputs, display error
   * messages if necessary, and authenticate the user.
   * @returns If the loginForm is invalid, the function will return and no further code will be executed.
   * If the username or password fields are empty, the function will return and no further code will be
   * executed. Otherwise, the function will continue executing the code to authenticate the user.
   */
  onSubmit() {
    this.submitted = true;
    this.alertService.clear();

    if (this.loginForm.invalid) {
      return;
    }

    if (this.f.username.value === "" || this.f.password.value === "") {
      this.msj = this.translate.instant('validation.required');
      this.alertService.error(this.msj);
      return;
    }

    this.loading = true;
    this.authenticateUser();
  }

  onChangeLang() {
    this.tovpilLang.translate(this.selectedLang.value);
  }

  /**
   * The `authenticateUser` function is used to authenticate a user by calling the `login` method of the
   * `authenticationService` and handling the response accordingly.
   */

  authenticateUser() {
    this.authenticationService.login(this.f.username.value, CryptoJS.MD5(this.f.password.value).toString())
      .pipe(first())
      .subscribe(
        data => {
          console.debug("Autenticación codigo: " + data.code);
          if (data.code === 'R-0200') {
            this.responseCode = '';
            this.router.navigate([this.returnUrl]);
            this.apiService.findSecurityById({ "id": data.tokenProfile }).then(data => {
              this.tovpilLang.translate(data.language);
            });
          } else if ((data.code === 'R-0600')) {
            this.responseCode = 'R-0600';
            this.f.username.setValue('');
            this.f.password.setValue('');
            this.loading = false;
            this.msj = this.translate.instant('validation.incorrect');
            this.alertService.error(this.msj);
          } else {
            this.responseCode = '';
            this.f.username.setValue('');
            this.f.password.setValue('');
            this.loading = false;
            this.msj = this.translate.instant('validation.incorrect');
            this.alertService.error(this.msj);
          }
        },
        error => {
          this.responseCode = 'R-1000';
          this.f.username.setValue('');
          this.f.password.setValue('');
          this.loading = false;
          this.msj = this.translate.instant('validation.incorrect');
          this.loading = false;
          this.msj = this.translate.instant('validation.error');
          this.alertService.error(this.msj);
        }
      );
  }
}
